import React, { useState } from 'react';
import {
  updateReleaseAppVersion,
  type ReleaseAppRow,
  type ReleaseAppVersionRow,
  type RuntimeConfigRow
} from '../api';
import { SectionCard } from './SectionCard';
import { StatusChip } from './Avatar';

export const reviewStatusCopy = {
  not_submitted: 'Not submitted',
  submitted: 'Submitted',
  in_review: 'In review',
  rejected: 'Rejected',
  approved: 'Approved',
  released: 'Released'
};
export type StoreReviewStatus = keyof typeof reviewStatusCopy;

export function reviewStatusTone(status: StoreReviewStatus) {
  if (status === 'approved' || status === 'released') return 'success' as const;
  if (status === 'rejected') return 'danger' as const;
  if (status === 'submitted' || status === 'in_review') return 'warn' as const;
  return 'neutral' as const;
}

function requestErrorMessage(error: unknown) {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = error.response;
    if (
      typeof response === 'object' &&
      response !== null &&
      'data' in response
    ) {
      const data = response.data;
      if (typeof data === 'object' && data !== null && 'message' in data) {
        if (typeof data.message === 'string') return data.message;
        if (Array.isArray(data.message)) return data.message.join('. ');
      }
    }
  }
  return error instanceof Error ? error.message : 'Could not save review mode.';
}

type Props = {
  app: ReleaseAppRow;
  version: ReleaseAppVersionRow;
  configs: RuntimeConfigRow[];
  token?: string;
  canManage: boolean;
  onSaved(): void;
};

export function StoreReviewControl({
  app,
  version,
  configs,
  token,
  canManage,
  onSaved
}: Props) {
  const enabled = version.storeReviewRoutingEnabled === true;
  const [reviewId, setReviewId] = useState(
    version.storeReviewRuntimeConfigId || ''
  );
  const [productionId, setProductionId] = useState(
    version.storeProductionRuntimeConfigId || ''
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const compatible = configs.filter(
    (config) =>
      config.organizationId === version.organizationId &&
      config.projectId === version.projectId &&
      (!config.platform || config.platform === app.platform)
  );
  const review = compatible.find(
    (config) =>
      config.runtimeConfigId === reviewId && config.environment === 'staging'
  );
  const production = compatible.find(
    (config) =>
      config.runtimeConfigId === productionId &&
      config.environment === 'production' &&
      (!config.releaseAppVersionId ||
        config.releaseAppVersionId === version.releaseAppVersionId)
  );

  async function save(nextEnabled: boolean) {
    if (!token || !canManage || busy) return;
    setBusy(true);
    setError('');
    try {
      await updateReleaseAppVersion(
        token,
        app.releaseAppId,
        version.releaseAppVersionId,
        {
          organizationId: version.organizationId,
          releaseAppId: app.releaseAppId,
          releaseAppVersionId: version.releaseAppVersionId,
          storeReviewStatus: nextEnabled ? 'in_review' : 'released',
          storeReviewRoutingEnabled: nextEnabled,
          storeReviewRuntimeConfigId: reviewId,
          storeProductionRuntimeConfigId: productionId
        }
      );
      onSaved();
    } catch (caught) {
      setError(requestErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SectionCard
      className="storeReviewControl"
      label="Store review mode"
      title={`${app.name} ${version.version}`}
      description="Applies to the selected production native app and store version. The separate staging app, other versions and website are unchanged."
      action={
        <StatusChip tone={enabled ? 'warn' : 'success'}>
          {enabled ? 'Review mode on · OTA off' : 'Review mode off'}
        </StatusChip>
      }
    >
      <div className="storeReviewNotice">
        In review mode, production-app bootstrap requests receive the staging
        API and its production OTA lane offers no updates. The separate staging
        app keeps its own API and normal OTA delivery. Finishing review restores
        the production API and OTA delivery.
      </div>
      <div className="storeReviewFields">
        <label className="storeReviewRuleField">
          <span>Staging API during review</span>
          <select
            aria-label="Staging API during review"
            value={reviewId}
            disabled={!canManage || busy}
            onChange={(event) => setReviewId(event.target.value)}
          >
            <option value="">Choose the staging API</option>
            {compatible
              .filter((config) => config.environment === 'staging')
              .map((config) => (
                <option
                  key={config.runtimeConfigId}
                  value={config.runtimeConfigId}
                >
                  {config.name} · {config.expressApiHostname}
                </option>
              ))}
          </select>
          {review ? <small>{review.expressApiHostname}</small> : null}
        </label>
        <label className="storeReviewRuleField">
          <span>Production API after review</span>
          <select
            aria-label="Production API after review"
            value={productionId}
            disabled={!canManage || busy}
            onChange={(event) => setProductionId(event.target.value)}
          >
            <option value="">Choose the production API</option>
            {compatible
              .filter(
                (config) =>
                  config.environment === 'production' &&
                  (!config.releaseAppVersionId ||
                    config.releaseAppVersionId === version.releaseAppVersionId)
              )
              .map((config) => (
                <option
                  key={config.runtimeConfigId}
                  value={config.runtimeConfigId}
                >
                  {config.name} · {config.expressApiHostname}
                </option>
              ))}
          </select>
          {production ? <small>{production.expressApiHostname}</small> : null}
        </label>
      </div>
      <div className="storeReviewNotice">
        Existing native clients do not need a new store release to receive this
        server policy. API routing changes when the app next fetches runtime
        configuration; cached or embedded settings can delay it. The OTA hold
        prevents update delivery, not update-check requests or locally staged
        updates. Clients that support OTA-disable also skip their checks.
      </div>
      {error ? (
        <div className="storeReviewNotice is-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="storeReviewActions">
        <button
          type="button"
          className="primaryButton"
          disabled={!canManage || !token || busy || !review || !production}
          onClick={() => void save(!enabled)}
        >
          {busy
            ? 'Saving…'
            : enabled
              ? 'Finish review — use production API and enable OTA'
              : 'Start review — use staging API and disable OTA'}
        </button>
      </div>
    </SectionCard>
  );
}
